import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { fetchPath, fetchProgress, fetchKanjiList, fetchRadicals, fetchRadicalFull } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import PhoneticAtlas from "@/pages/PhoneticAtlas";

const LEVELS = [
  { id: "N5", color: "#4A6D7C" },
  { id: "N4", color: "#6E7A5A" },
  { id: "N3", color: "#D29C4C" },
  { id: "N2", color: "#B86C45" },
  { id: "N1", color: "#8B4546" },
];
// Stroke-count navigation buckets. Singles 1–9, then 10–14 individually
// (each has plenty of kanji), then 15+ pooled (everything from 15 strokes
// onward in one bucket — fewer kanji per count up there).
const STROKE_BUCKETS = [
  { id: 1, label: "1" },
  { id: 2, label: "2" },
  { id: 3, label: "3" },
  { id: 4, label: "4" },
  { id: 5, label: "5" },
  { id: 6, label: "6" },
  { id: 7, label: "7" },
  { id: 8, label: "8" },
  { id: 9, label: "9" },
  { id: 10, label: "10" },
  { id: 11, label: "11" },
  { id: 12, label: "12" },
  { id: 13, label: "13" },
  { id: 14, label: "14" },
  { id: 15, label: "15+", range: [15, 99] },
];

const STATUS_COLOR = { known: "#6E7A5A", learning: "#D29C4C", new: "#D1CCC5" };
const STATUS_BG = {
  known:    "rgba(110,122,90,0.14)",
  learning: "rgba(210,156,76,0.16)",
  new:      "transparent",
};

function ProgressBar({ stats }) {
  const total = stats.total || 0;
  const known = stats.known || 0;
  const learning = stats.learning || 0;
  const pctK = total ? (known / total) * 100 : 0;
  const pctL = total ? (learning / total) * 100 : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="h-1.5 bg-borderDefault relative overflow-hidden">
        <div className="absolute inset-y-0 left-0" style={{ width: `${pctK}%`, background: STATUS_COLOR.known }} />
        <div
          className="absolute inset-y-0"
          style={{ left: `${pctK}%`, width: `${pctL}%`, background: STATUS_COLOR.learning }}
        />
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-sumiSoft font-sans">
        {known} known · {learning} learning · {total - known - learning} new
      </p>
    </div>
  );
}

function KanjiTile({ k, status, onClick }) {
  const s = status || "new";
  const ring = STATUS_COLOR[s];
  const bg = STATUS_BG[s];
  return (
    <motion.button
      layout
      data-testid={TEST_IDS.pathKanji(k.kanji)}
      onClick={onClick}
      whileHover={{ y: -2 }}
      className="relative flex flex-col items-center justify-center w-[72px] h-[92px] transition-colors hover:border-shu"
      style={{
        borderColor: ring,
        borderWidth: s === "new" ? 1 : 2,
        borderStyle: "solid",
        background: bg || "transparent",
      }}
    >
      {s !== "new" && (
        <span
          aria-hidden
          className="absolute top-1 right-1 rounded-full"
          style={{ width: 8, height: 8, background: ring }}
        />
      )}
      <span className="font-serif text-3xl text-sumi leading-none">{k.kanji}</span>
      <span className="text-[10px] text-sumiSoft font-sans mt-1 px-1 text-center leading-tight line-clamp-2">
        {k.meaning}
      </span>
    </motion.button>
  );
}

function RadicalRow({ rad, expanded, onToggle, progress, onSelectKanji }) {
  // One row in the radicals list. Closed: meta only. Open: kanji tiles
  // appear underneath as a `flex-wrap` strip (same widget as JLPT mode).
  // When the user clicks "show all" inside an expanded row we fetch
  // the full member list via /api/radical/{char}; until then we render
  // the truncated `rad.kanji` slice that came with the initial list.
  const [showAll, setShowAll] = useState(false);
  // Reset show-all whenever the row collapses — re-expanding a row a
  // second time should start from the truncated view again rather than
  // jumping straight to 100+ tiles.
  useEffect(() => {
    if (!expanded) setShowAll(false);
  }, [expanded]);
  const fullQ = useQuery({
    queryKey: ["radical-full", rad.char],
    queryFn: () => fetchRadicalFull(rad.char),
    enabled: showAll,
    staleTime: Infinity,
  });
  const truncated = rad.kanji.length < rad.kanji_count;
  const visibleKanji = showAll && fullQ.data?.kanji
    ? fullQ.data.kanji
    : rad.kanji;
  return (
    <motion.section
      layout
      data-testid={`radical-row-${rad.char}`}
      className="border border-borderDefault mb-3"
    >
      {/* Header row: radical glyph + meaning link to its parent kanji
          card (e.g. 氵 → /kanji/水, since 氵 is the side-form of 水).
          A separate chevron button on the right toggles the expansion
          to show the first-level kanji that contain this radical. */}
      <div className="flex items-stretch hover:bg-washiSoft transition-colors">
        {rad.parent_kanji ? (
          <Link
            to={`/kanji/${encodeURIComponent(rad.parent_kanji)}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              if (onSelectKanji) {
                e.preventDefault();
                onSelectKanji(rad.parent_kanji);
              }
            }}
            data-testid={`radical-link-${rad.char}`}
            title={
              rad.parent_kanji === rad.char
                ? `open ${rad.char} card`
                : `open card for ${rad.parent_kanji} (parent kanji of radical ${rad.char})`
            }
            className="flex-1 flex items-center gap-4 px-3 py-2.5 text-left min-w-0"
          >
            <span className="font-serif text-3xl text-sumi w-10 text-center leading-none">
              {rad.char}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-serif text-sm text-sumi truncate">
                {rad.meaning || <em className="text-sumiSoft">unnamed radical</em>}
                {rad.parent_kanji && rad.parent_kanji !== rad.char && (
                  <span className="text-sumiSoft font-sans text-[10px] uppercase tracking-[0.2em] ml-2">
                    → {rad.parent_kanji}
                  </span>
                )}
              </span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-sumiSoft font-sans">
                {rad.strokes != null ? `${rad.strokes} stroke${rad.strokes === 1 ? "" : "s"} · ` : ""}
                {rad.kanji_count} kanji
                {rad.kanji.length < rad.kanji_count && ` · showing ${rad.kanji.length}`}
              </span>
            </span>
          </Link>
        ) : (
          <div
            className="flex-1 flex items-center gap-4 px-3 py-2.5 text-left min-w-0"
            title="no parent kanji — ghost radical"
          >
            <span className="font-serif text-3xl text-sumi w-10 text-center leading-none">
              {rad.char}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-serif text-sm text-sumi truncate">
                {rad.meaning || <em className="text-sumiSoft">unnamed radical</em>}
              </span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-sumiSoft font-sans">
                {rad.strokes != null ? `${rad.strokes} stroke${rad.strokes === 1 ? "" : "s"} · ` : ""}
                {rad.kanji_count} kanji
                {rad.kanji.length < rad.kanji_count && ` · showing ${rad.kanji.length}`}
              </span>
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          data-testid={`radical-toggle-${rad.char}`}
          aria-expanded={expanded}
          aria-label={expanded ? `collapse ${rad.char}` : `expand ${rad.char} to show kanji`}
          className="px-4 border-l border-borderDefault text-sumiSoft hover:text-shu hover:bg-shu/5 transition-colors flex items-center"
        >
          <span
            aria-hidden
            className="text-xs transition-transform"
            style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            ▶
          </span>
        </button>
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="kanji-strip"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div
              data-testid={`radical-kanji-${rad.char}`}
              className="flex flex-wrap gap-2 px-3 pb-3 pt-1"
            >
              {visibleKanji.map((k) => (
                <KanjiTile
                  key={k.kanji}
                  k={k}
                  status={progress[k.kanji]?.status}
                  onClick={() => onSelectKanji?.(k.kanji)}
                />
              ))}
              {truncated && !showAll && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  data-testid={`radical-show-all-${rad.char}`}
                  className="flex flex-col items-center justify-center w-[72px] h-[92px] border border-dashed border-shu text-shu hover:bg-shu hover:text-washi transition-colors text-[10px] uppercase tracking-[0.18em] font-sans px-1 text-center leading-tight"
                  title={`show all ${rad.kanji_count} kanji`}
                >
                  show all
                  <span className="font-serif text-base mt-0.5">+{rad.kanji_count - rad.kanji.length}</span>
                </button>
              )}
              {showAll && fullQ.isLoading && (
                <span
                  data-testid={`radical-loading-${rad.char}`}
                  className="flex items-center justify-center w-[72px] h-[92px] text-sumiSoft text-[10px] uppercase tracking-[0.18em] font-sans animate-pulse"
                >
                  loading…
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

export default function BuildPath({ onSelectKanji }) {
  // `mode` decides whether we navigate by JLPT level (the launch-day
  // story arc), by stroke count (a "find any kanji by shape" entry
  // point for learners who don't know meaning or sound), or by radical
  // (every productive radical → kanji that contain it, progressive tree).
  const [mode, setMode] = useState("jlpt");
  const [level, setLevel] = useState("N5");
  const [stroke, setStroke] = useState(1);
  // Secondary sort inside Strokes mode. 'freq' (default) shows the most
  // common kanji first — ideal for general browsing. 'radical' groups by
  // primary radical character — ideal for "I saw this kanji and the left
  // half looks like 氵 / 言 / 木".
  const [strokeSort, setStrokeSort] = useState("freq");
  // Sort order for the Radicals list. 'freq' = most kanji-producing
  // first (default — backend already returns this order). 'strokes' =
  // ascending stroke count of the radical itself, useful for learners
  // who want to start from the simplest shapes (一, 丶, 丿 …).
  const [radSort, setRadSort] = useState("freq");
  const [expandedRadical, setExpandedRadical] = useState(null);
  // Anchor for scroll-to-top: switching bucket/sort/mode dumps the user
  // at the top of the new list rather than leaving them stranded mid-page.
  const listAnchorRef = useRef(null);

  const pathQ = useQuery({
    queryKey: ["path", level],
    queryFn: () => fetchPath(level),
    enabled: mode === "jlpt",
  });
  const allQ = useQuery({
    queryKey: ["kanji-all"],
    queryFn: () => fetchKanjiList({ level: "ALL", limit: 2200 }),
    enabled: mode === "strokes",
    staleTime: Infinity,
  });
  const radQ = useQuery({
    queryKey: ["radicals"],
    queryFn: () => fetchRadicals({ min_kanji: 3, per_radical: 24 }),
    enabled: mode === "radicals",
    staleTime: Infinity,
  });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });
  const progress = progQ.data?.items || {};

  // For JLPT mode, stages come pre-built from /api/path. For Strokes mode,
  // we build stages client-side: one stage per stroke count, kanji sorted
  // by frequency within each stage (the backend already sorts by freq).
  const stages = useMemo(() => {
    if (mode === "jlpt") return pathQ.data?.stages || [];
    if (mode === "radicals" || mode === "phonetic") return []; // these modes use their own render paths
    const all = allQ.data?.items || [];
    const sortKanji = (arr) => {
      if (strokeSort === "radical") {
        return [...arr].sort((a, b) => {
          const ra = a.radical || a.kanji;
          const rb = b.radical || b.kanji;
          if (ra === rb) return a.kanji.localeCompare(b.kanji);
          return ra.localeCompare(rb, "ja");
        });
      }
      return arr; // backend already sorts by freq
    };
    if (stroke === 15) {
      const grouped = {};
      for (const k of all) {
        if (k.strokes == null || k.strokes < 15) continue;
        (grouped[k.strokes] ||= []).push(k);
      }
      return Object.keys(grouped)
        .map(Number)
        .sort((a, b) => a - b)
        .map((s) => ({
          stage: s,
          title: `${s}-stroke kanji`,
          kanji: sortKanji(grouped[s]),
        }));
    }
    return [{
      stage: stroke,
      title: `${stroke}-stroke kanji`,
      kanji: sortKanji(all.filter((k) => k.strokes === stroke)),
    }];
  }, [mode, pathQ.data, allQ.data, stroke, strokeSort]);

  // Radicals are returned freq-sorted by the backend. When the learner
  // picks 'strokes', we re-sort by ascending stroke count of the radical
  // itself, pushing unknown-stroke entries (rare positional variants we
  // haven't curated) to the end so they don't lead the list.
  const radicals = useMemo(() => {
    const items = radQ.data?.items || [];
    if (radSort === "strokes") {
      return [...items].sort((a, b) => {
        const sa = a.strokes ?? 99;
        const sb = b.strokes ?? 99;
        if (sa !== sb) return sa - sb;
        return b.kanji_count - a.kanji_count;
      });
    }
    return items;
  }, [radQ.data, radSort]);

  const stats = useMemo(() => {
    if (mode === "radicals") {
      // Dedupe — a kanji can sit under multiple radicals (e.g. 校 → 木 + 交).
      // We want one count per kanji, not one per (radical, kanji) pair.
      const seen = new Set();
      let known = 0, learning = 0;
      for (const r of radicals) {
        for (const k of r.kanji) {
          if (seen.has(k.kanji)) continue;
          seen.add(k.kanji);
          const st = progress[k.kanji]?.status;
          if (st === "known") known++;
          else if (st === "learning") learning++;
        }
      }
      return { total: seen.size, known, learning };
    }
    const items = stages.flatMap((s) => s.kanji);
    let known = 0, learning = 0;
    for (const k of items) {
      const st = progress[k.kanji]?.status;
      if (st === "known") known++;
      else if (st === "learning") learning++;
    }
    return { total: items.length, known, learning };
  }, [mode, stages, radicals, progress]);

  const isLoading = mode === "jlpt"
    ? pathQ.isLoading
    : mode === "strokes"
      ? allQ.isLoading
      : mode === "radicals"
        ? radQ.isLoading
        : false; // phonetic mode renders PhoneticAtlas which manages its own loading

  // Scroll the list back to the top whenever the user changes mode /
  // bucket / sort — long lists otherwise leave them stranded mid-page.
  // BUT skip the very first render: on initial page load the user
  // expects to see the header + filter menus, not be auto-scrolled past
  // them straight to the list.
  const firstScrollRef = useRef(true);
  useEffect(() => {
    if (firstScrollRef.current) {
      firstScrollRef.current = false;
      return;
    }
    if (listAnchorRef.current) {
      listAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [mode, level, stroke, strokeSort, radSort]);

  return (
    <div className="overflow-y-auto p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Mode toggle */}
        <div className="flex items-center gap-2 mb-3" data-testid="path-mode-toggle">
          <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">browse by</span>
          <div className="inline-flex border border-borderDefault">
            <button
              data-testid="path-mode-jlpt"
              onClick={() => setMode("jlpt")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-sans transition-colors ${
                mode === "jlpt" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
              }`}
            >
              JLPT
            </button>
            <button
              data-testid="path-mode-strokes"
              onClick={() => setMode("strokes")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-sans border-l border-borderDefault transition-colors ${
                mode === "strokes" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
              }`}
            >
              Strokes
            </button>
            <button
              data-testid="path-mode-radicals"
              onClick={() => setMode("radicals")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-sans border-l border-borderDefault transition-colors ${
                mode === "radicals" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
              }`}
            >
              Radicals
            </button>
            <button
              data-testid="path-mode-phonetic"
              onClick={() => setMode("phonetic")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-sans border-l border-borderDefault transition-colors ${
                mode === "phonetic" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
              }`}
            >
              Phonetic
            </button>
          </div>
        </div>

        {/* The active bucket row — JLPT levels, stroke counts, or radical sort */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          {mode === "jlpt" && (
            <div data-testid={TEST_IDS.levelSelector} className="flex border border-borderDefault overflow-x-auto">
              {LEVELS.map((l) => {
                const active = level === l.id;
                return (
                  <button
                    key={l.id}
                    data-testid={TEST_IDS.levelOption(l.id)}
                    onClick={() => setLevel(l.id)}
                    className={`px-3 md:px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-sans border-l first:border-l-0 border-borderDefault transition-colors ${
                      active ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                    }`}
                  >
                    {l.id}
                  </button>
                );
              })}
            </div>
          )}
          {mode === "strokes" && (
            <div className="flex items-center gap-2 flex-wrap">
              <div data-testid="stroke-selector" className="flex border border-borderDefault overflow-x-auto">
                {STROKE_BUCKETS.map((b) => {
                  const active = stroke === b.id;
                  return (
                    <button
                      key={b.id}
                      data-testid={`stroke-option-${b.id}`}
                      onClick={() => setStroke(b.id)}
                      className={`px-2.5 md:px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-sans border-l first:border-l-0 border-borderDefault transition-colors ${
                        active ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                      }`}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">sort</span>
              <div data-testid="stroke-sort-toggle" className="inline-flex border border-borderDefault">
                <button
                  data-testid="stroke-sort-freq"
                  onClick={() => setStrokeSort("freq")}
                  className={`px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans transition-colors ${
                    strokeSort === "freq" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                  }`}
                >
                  freq
                </button>
                <button
                  data-testid="stroke-sort-radical"
                  onClick={() => setStrokeSort("radical")}
                  className={`px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans border-l border-borderDefault transition-colors ${
                    strokeSort === "radical" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                  }`}
                >
                  radical
                </button>
              </div>
            </div>
          )}
          {mode === "radicals" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">sort radicals by</span>
              <div data-testid="radical-sort-toggle" className="inline-flex border border-borderDefault">
                <button
                  data-testid="radical-sort-freq"
                  onClick={() => setRadSort("freq")}
                  className={`px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans transition-colors ${
                    radSort === "freq" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                  }`}
                >
                  freq
                </button>
                <button
                  data-testid="radical-sort-strokes"
                  onClick={() => setRadSort("strokes")}
                  className={`px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans border-l border-borderDefault transition-colors ${
                    radSort === "strokes" ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                  }`}
                >
                  strokes
                </button>
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">
                {radicals.length} radicals
              </span>
            </div>
          )}
          {mode !== "phonetic" && (
            <div className="w-full md:w-64">
              <ProgressBar stats={stats} />
            </div>
          )}
        </div>

        {mode !== "phonetic" && (
          <div className="flex items-center gap-3 flex-wrap mb-4 text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft" data-testid="path-legend">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR.known }} />
              known
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR.learning }} />
              learning
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full border" style={{ borderColor: STATUS_COLOR.new }} />
              new
            </span>
          </div>
        )}

        <p className="font-sans text-sm text-sumiSoft mb-6 max-w-2xl">
          {mode === "jlpt" && "Walk from primitives up to complex kanji. Pick any tile to open its full card — meaning, readings, components, and a tree of how it builds up."}
          {mode === "strokes" && "Don't know the meaning or sound? Just count the strokes. Useful for looking up a kanji you've seen on a sign or in a manga."}
          {mode === "radicals" && "Every productive radical, top-down by how many kanji it grows into. Tap any radical to reveal the kanji that contain it."}
          {mode === "phonetic" && "Every phonetic driver, grouped by reading. A kanji you can't quite remember? Find one of its sound-relatives here."}
        </p>

        <div ref={listAnchorRef} aria-hidden className="h-0 -mt-2" />

        {mode === "phonetic" && (
          <PhoneticAtlas embedded onSelectKanji={onSelectKanji} />
        )}

        {mode !== "phonetic" && isLoading && (
          <p className="text-sumiSoft font-sans text-sm animate-pulse">laying the path…</p>
        )}

        {mode === "radicals" && !isLoading && (
          <div data-testid="radicals-list">
            {radicals.map((rad) => (
              <RadicalRow
                key={rad.char}
                rad={rad}
                expanded={expandedRadical === rad.char}
                onToggle={() =>
                  setExpandedRadical((prev) => (prev === rad.char ? null : rad.char))
                }
                progress={progress}
                onSelectKanji={onSelectKanji}
              />
            ))}
          </div>
        )}

        {mode !== "radicals" && stages.map((stage, idx) => (
          <motion.section
            key={stage.stage}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: idx * 0.04 }}
            data-testid={TEST_IDS.stageCard(stage.stage)}
            className="mb-8"
          >
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-serif text-shu text-2xl">{String(stage.stage).padStart(2, "0")}</span>
              <h3 className="font-serif text-lg text-sumi">{stage.title}</h3>
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">
                {stage.kanji.length} kanji
              </span>
              <span className="hairline h-px flex-1 mt-3" />
            </div>
            <div className="flex flex-wrap gap-2">
              {stage.kanji.map((k) => (
                <KanjiTile
                  key={k.kanji}
                  k={k}
                  status={progress[k.kanji]?.status}
                  onClick={() => onSelectKanji?.(k.kanji)}
                />
              ))}
            </div>
          </motion.section>
        ))}
      </div>
    </div>
  );
}
